import { GLOBAL } from 'saddlebag-browser';

import type { TimeoutID } from '@web-platform/shared-universal-types-utils/src/utils';

type RequestIdleCallback = typeof window.requestIdleCallback;
type CancelIdleCallback = typeof window.cancelIdleCallback;

type CombinedTimerID = TimeoutID | ReturnType<RequestIdleCallback> | ReturnType<CancelIdleCallback>;

const $window = GLOBAL.getWindow();

// If this function is used in other contexts, we might have to introduce other types for the different numbers of arguments, see https://github.com/facebook/flow/issues/854
export const delayAndDebounce = <T, U>(
  workFunc: (arg1: T, arg2: U) => unknown,
): ((arg1: T, arg2: U) => CombinedTimerID) => {
  const windowRequestIdleCallback: RequestIdleCallback | undefined = $window?.requestIdleCallback;
  const windowCancelIdleCallback: CancelIdleCallback | undefined = $window?.cancelIdleCallback;

  let handle: CombinedTimerID | null = null;

  // use setTimeout if requestIdleCallback is not supported
  const delay = windowRequestIdleCallback
    ? (callBack: IdleRequestCallback) =>
        windowRequestIdleCallback(callBack, {
          timeout: 1,
        })
    : setTimeout;

  // compatible with lower versions without requestIdleCallback
  const cancelDelay = windowCancelIdleCallback || clearTimeout;

  return (...rest) => {
    // actually in browser setTimeout return a value with number type
    if (handle && typeof handle === 'number') {
      cancelDelay(handle);
    }

    handle = delay(() => {
      handle = null;
      workFunc(...rest);
    });
    return handle;
  };
};
